var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as React from 'react';
import { Box, Checkbox, Chip, Collapse, IconButton, Stack, Typography, } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import { formatPhoneInput } from '../config/form_field_factories';
import { CONTACT_ADD_TILE_SX, CONTACT_CARD_SX, CONTACT_EMPTY_STATE_SX, CONTACT_HEADER_SX, CONTACT_PANEL_SX, CONTACT_SCROLLER_SX, CONTACT_TITLE_CHIP_SX, contactTileSx, } from './ScheduleTakedownStyles';
var ExistingContactSelection = function (_a) {
    var _b = _a.title, title = _b === void 0 ? 'Select existing business contacts' : _b, contacts = _a.contacts, selectedContactId = _a.selectedContactId, onSelect = _a.onSelect, onAddNew = _a.onAddNew;
    var _c = React.useState(true), isExpanded = _c[0], setIsExpanded = _c[1];
    var toggleExpanded = React.useCallback(function () { return setIsExpanded(function (wasExpanded) { return !wasExpanded; }); }, []);
    return (React.createElement(Box, { sx: { mb: 2 } },
        React.createElement(Box, { sx: CONTACT_CARD_SX },
            React.createElement(Box, { role: "button", tabIndex: 0, "aria-expanded": isExpanded, onClick: toggleExpanded, onKeyDown: function (event) {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        toggleExpanded();
                    }
                }, sx: CONTACT_HEADER_SX },
                React.createElement(Typography, { sx: { fontSize: '14px', fontWeight: 500, color: '#21242A' } }, title),
                React.createElement(IconButton, { size: "small", sx: { color: '#273167' }, "aria-label": isExpanded ? 'Hide contacts' : 'Show contacts' }, isExpanded ? React.createElement(KeyboardArrowUpIcon, null) : React.createElement(KeyboardArrowDownIcon, null))),
            React.createElement(Collapse, { in: isExpanded, timeout: "auto", unmountOnExit: true },
                React.createElement(Box, { sx: CONTACT_PANEL_SX },
                    React.createElement(Box, { sx: CONTACT_SCROLLER_SX }, contacts.length === 0 ? (React.createElement(Box, { sx: CONTACT_EMPTY_STATE_SX },
                        React.createElement(Typography, { fontSize: "13px", color: "text.secondary" }, "There are no contacts on this record. Please add a new document signer."))) : (React.createElement(Box, { sx: { display: 'flex', gap: 1.5, width: 'max-content' } }, contacts.map(function (contact) {
                        var isSelected = contact.id === selectedContactId;
                        var fullName = [
                            contact.firstName,
                            contact.lastName,
                            contact.suffix,
                        ]
                            .filter(Boolean)
                            .join(' ');
                        return (React.createElement(Box, { key: contact.id, onClick: function () {
                                return onSelect(isSelected ? null : contact);
                            }, sx: contactTileSx(isSelected) },
                            React.createElement(Stack, { direction: "row", justifyContent: "space-between", alignItems: "flex-start", mb: 0.75 },
                                React.createElement(Typography, { sx: {
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        lineHeight: 1.3,
                                        pr: 1,
                                    } }, fullName || '--'),
                                React.createElement(Checkbox, { checked: isSelected, size: "small", sx: { p: 0, flexShrink: 0 }, inputProps: {
                                        'aria-label': "Use ".concat(fullName, " as the document signer"),
                                    }, onClick: function (event) {
                                        return event.stopPropagation();
                                    }, onChange: function (event) {
                                        return onSelect(event.target.checked
                                            ? contact
                                            : null);
                                    } })),
                            contact.titleLabel && (React.createElement(Chip, { label: contact.titleLabel, size: "small", sx: CONTACT_TITLE_CHIP_SX })),
                            React.createElement(Stack, { direction: "row", alignItems: "center", gap: "6px", mt: 0.25 },
                                React.createElement(EmailOutlinedIcon, { sx: {
                                        fontSize: 15,
                                        color: '#6B7280',
                                        flexShrink: 0,
                                    } }),
                                React.createElement(Typography, { sx: {
                                        fontSize: '12px',
                                        color: '#4B5568',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    } }, contact.email || '--')),
                            React.createElement(Stack, { direction: "row", alignItems: "center", gap: "6px", mt: 0.4 },
                                React.createElement(LocalPhoneOutlinedIcon, { sx: {
                                        fontSize: 15,
                                        color: '#6B7280',
                                        flexShrink: 0,
                                    } }),
                                React.createElement(Typography, { sx: { fontSize: '12px', color: '#4B5568' } }, contact.phone || '--'))));
                    })))),
                    React.createElement(Box, { role: "button", tabIndex: 0, onClick: onAddNew, onKeyDown: function (event) {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                onAddNew();
                            }
                        }, sx: CONTACT_ADD_TILE_SX },
                        React.createElement(AddCircleOutlineIcon, { sx: { fontSize: 26, color: '#20285C' } }),
                        React.createElement(Typography, { sx: {
                                fontSize: '13px',
                                fontWeight: 600,
                                color: '#20285C',
                                textAlign: 'center',
                                lineHeight: 1.3,
                            } }, "Add Doc Signer")))))));
};
var FIRST_RECORD_INDEX = 0;
export var useExistingContactSlots = function (_a) {
    var recordsRef = _a.recordsRef, sectionId = _a.sectionId, signerFields = _a.signerFields;
    var _b = React.useState({}), contactsByRecord = _b[0], setContactsByRecord = _b[1];
    var _c = React.useState({}), selectedContactIdByRecord = _c[0], setSelectedContactIdByRecord = _c[1];
    var handleContactSelect = React.useCallback(function (recordIndex, contact) {
        var _a;
        var _b, _c, _d, _e, _f, _g, _h, _j;
        setSelectedContactIdByRecord(function (previous) {
            var _a;
            var _b;
            return (__assign(__assign({}, previous), (_a = {}, _a[recordIndex] = (_b = contact === null || contact === void 0 ? void 0 : contact.id) !== null && _b !== void 0 ? _b : '', _a)));
        });
        (_b = recordsRef.current) === null || _b === void 0 ? void 0 : _b.mergeRecord(recordIndex, (_a = {},
            _a[signerFields.contactAspireOId] = (_c = contact === null || contact === void 0 ? void 0 : contact.id) !== null && _c !== void 0 ? _c : '',
            _a[signerFields.firstName] = (_d = contact === null || contact === void 0 ? void 0 : contact.firstName) !== null && _d !== void 0 ? _d : '',
            _a[signerFields.middleName] = (_e = contact === null || contact === void 0 ? void 0 : contact.middleName) !== null && _e !== void 0 ? _e : '',
            _a[signerFields.lastName] = (_f = contact === null || contact === void 0 ? void 0 : contact.lastName) !== null && _f !== void 0 ? _f : '',
            _a[signerFields.suffix] = (_g = contact === null || contact === void 0 ? void 0 : contact.suffix) !== null && _g !== void 0 ? _g : '',
            _a[signerFields.title] = (_h = contact === null || contact === void 0 ? void 0 : contact.titleValue) !== null && _h !== void 0 ? _h : '',
            _a[signerFields.email] = (_j = contact === null || contact === void 0 ? void 0 : contact.email) !== null && _j !== void 0 ? _j : '',
            _a[signerFields.phone] = (contact === null || contact === void 0 ? void 0 : contact.phone) ? formatPhoneInput(contact.phone) : '',
            _a));
    }, [recordsRef, signerFields]);
    var handleAddNewContact = React.useCallback(function (recordIndex) { return handleContactSelect(recordIndex, null); }, [handleContactSelect]);
    var beginContactsReport = React.useCallback(function () {
        var _a, _b;
        var recordIndex = (_b = (_a = recordsRef.current) === null || _a === void 0 ? void 0 : _a.getActiveRecordIndex()) !== null && _b !== void 0 ? _b : FIRST_RECORD_INDEX;
        return function (contacts) {
            setContactsByRecord(function (previous) {
                var _a;
                return (__assign(__assign({}, previous), (_a = {}, _a[recordIndex] = contacts, _a)));
            });
            setSelectedContactIdByRecord(function (previous) {
                var _a;
                return (__assign(__assign({}, previous), (_a = {}, _a[recordIndex] = '', _a)));
            });
        };
    }, [recordsRef]);
    var startContactsForRecord = React.useCallback(function (recordIndex, contacts) {
        var _a;
        return setContactsByRecord((_a = {}, _a[recordIndex] = contacts, _a));
    }, []);
    var slotsByRecord = React.useMemo(function () {
        return Object.entries(contactsByRecord).reduce(function (slots, _a) {
            var _b;
            var _c;
            var recordKey = _a[0], contacts = _a[1];
            if (!contacts.length) {
                return slots;
            }
            var recordIndex = Number(recordKey);
            slots[recordIndex] = (_b = {},
                _b[sectionId] = (React.createElement(ExistingContactSelection, { contacts: contacts, selectedContactId: (_c = selectedContactIdByRecord[recordIndex]) !== null && _c !== void 0 ? _c : '', onSelect: function (contact) { return handleContactSelect(recordIndex, contact); }, onAddNew: function () { return handleAddNewContact(recordIndex); } })),
                _b);
            return slots;
        }, {});
    }, [
        contactsByRecord,
        handleAddNewContact,
        handleContactSelect,
        sectionId,
        selectedContactIdByRecord,
    ]);
    var getSectionSlots = React.useCallback(function (recordIndex) { return slotsByRecord[recordIndex]; }, [slotsByRecord]);
    var clearContacts = React.useCallback(function (recordIndex) {
        if (recordIndex === undefined) {
            setContactsByRecord({});
            setSelectedContactIdByRecord({});
            return;
        }
        var withoutRecord = function (previous) {
            var next = __assign({}, previous);
            delete next[recordIndex];
            return next;
        };
        setContactsByRecord(withoutRecord);
        setSelectedContactIdByRecord(withoutRecord);
    }, []);
    return { beginContactsReport: beginContactsReport, startContactsForRecord: startContactsForRecord, getSectionSlots: getSectionSlots, clearContacts: clearContacts };
};
export default React.memo(ExistingContactSelection);
